/**
 * BishopManjoroPage - Public page for Faith World Ministries & Bishop Dr. B. Manjoro
 * Displays bishop bio, headquarters, all branches, gallery, and store items
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { churchesAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Church, MapPin, Globe, BookOpen, ExternalLink, ChevronRight,
  Image as ImageIcon, ShoppingBag, Users, Award, ArrowLeft, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BishopManjoroPage = () => {
  const navigate = useNavigate();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['bishop-manjoro'],
    queryFn: () => churchesAPI.bishopManjoro().then(r => r.data || r),
    staleTime: 60000,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto p-6 space-y-8">
          <Skeleton className="h-80 w-full rounded-2xl" />
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Church className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Unable to load ministry information</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/churches')}>
            View Churches Directory
          </Button>
        </div>
      </div>
    );
  }

  const { bishop, headquarters, branches, countries, store_items, gallery } = data;

  return (
    <div className="min-h-screen bg-[#0f1419]" data-testid="bishop-manjoro-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#0f1419]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/churches')}
            className="text-amber-400 hover:text-amber-300 flex items-center gap-2 text-sm font-medium transition-colors"
            data-testid="back-to-churches"
          >
            <ArrowLeft className="h-4 w-4" />
            Churches Directory
          </button>
          <a
            href={data.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400/70 hover:text-amber-300 flex items-center gap-1 text-sm transition-colors"
          >
            bishopmanjoro.org <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </nav>

      {/* Hero - Bishop Bio */}
      <section className="relative overflow-hidden" data-testid="bishop-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f2e] via-[#0f1419] to-[#1a1f2e]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj48cGF0aCBkPSJNMzAgMGwzMCA2MEgweiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDIxMiwxNzUsNTUsMC4wMykiIHN0cm9rZS13aWR0aD0iMSIvPjwvc3ZnPg==')] opacity-40" />
        <div className="relative max-w-6xl mx-auto px-4 py-16">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            {/* Bishop Photo */}
            <div className="shrink-0">
              <div className="w-56 h-72 rounded-2xl overflow-hidden border-2 border-amber-500/30 shadow-2xl shadow-amber-900/20">
                <img
                  src={bishop.photo_url}
                  alt={bishop.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-amber-900/40 to-slate-800 flex items-center justify-center"><svg class="h-16 w-16 text-amber-500/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                  }}
                />
              </div>
            </div>

            {/* Bio */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
                <Award className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-xs text-amber-400 font-medium tracking-wide uppercase">Founder & President</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2 tracking-tight">{bishop.name}</h1>
              <p className="text-amber-400/80 text-base mb-6">{bishop.title}</p>
              <p className="text-slate-400 leading-relaxed text-sm max-w-2xl">{bishop.bio}</p>

              {/* Books */}
              <div className="mt-6 flex flex-wrap gap-2 justify-center lg:justify-start">
                <span className="text-xs text-slate-500 mr-1 self-center">Published:</span>
                {bishop.books.map((book, i) => (
                  <Badge key={i} variant="outline" className="border-amber-500/30 text-amber-300/80 bg-amber-500/5 text-xs">
                    {book}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#151a24] border-y border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-amber-400">{data.total_branches}</div>
              <div className="text-xs text-slate-500 mt-1">Churches & Colleges</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">{countries?.length || 0}</div>
              <div className="text-xs text-slate-500 mt-1">Countries</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">1993</div>
              <div className="text-xs text-slate-500 mt-1">Year Founded</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">{gallery?.length || 0}+</div>
              <div className="text-xs text-slate-500 mt-1">Gallery Photos</div>
            </div>
          </div>
        </div>
      </section>

      {/* Headquarters */}
      {headquarters && (
        <section className="max-w-6xl mx-auto px-4 py-12" data-testid="headquarters-section">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Church className="h-5 w-5 text-amber-400" />
            Headquarters
          </h2>
          <Card className="bg-[#1a1f2e] border-white/10 overflow-hidden">
            <CardContent className="p-0">
              <div className="flex flex-col md:flex-row">
                {headquarters.logo_url && (
                  <div className="md:w-72 h-48 md:h-auto bg-slate-800/50 flex items-center justify-center p-6">
                    <img
                      src={headquarters.logo_url}
                      alt={headquarters.name}
                      className="max-h-full max-w-full object-contain rounded-lg"
                    />
                  </div>
                )}
                <div className="flex-1 p-6">
                  <h3 className="text-xl font-semibold text-white mb-2">{headquarters.name}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{headquarters.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {headquarters.location && (
                      <span className="flex items-center gap-1.5 text-slate-400">
                        <MapPin className="h-3.5 w-3.5 text-amber-400" />
                        {headquarters.location}
                      </span>
                    )}
                    {headquarters.website_url && (
                      <a
                        href={headquarters.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-amber-400 hover:text-amber-300 transition-colors"
                      >
                        <Globe className="h-3.5 w-3.5" />
                        Website
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Branches */}
      <section className="max-w-6xl mx-auto px-4 py-12" data-testid="branches-section">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Globe className="h-5 w-5 text-amber-400" />
          International Branches
          <Badge variant="outline" className="border-amber-500/30 text-amber-300 ml-2 text-xs">
            {branches?.length || 0} locations
          </Badge>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches?.map((branch) => (
            <Card
              key={branch.id}
              className="bg-[#1a1f2e] border-white/10 hover:border-amber-500/30 transition-all group cursor-pointer"
              data-testid={`branch-${branch.id}`}
              onClick={() => navigate(`/churches/${branch.id}`)}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                    <Church className="h-5 w-5 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-white truncate group-hover:text-amber-300 transition-colors">
                      {branch.name}
                    </h3>
                    <div className="flex items-center gap-1.5 text-slate-500 text-xs mt-1">
                      <MapPin className="h-3 w-3" />
                      {branch.location || `${branch.city || ''}, ${branch.country || ''}`}
                    </div>
                    {branch.continent && (
                      <Badge variant="outline" className="mt-2 text-[10px] border-white/10 text-slate-400">
                        {branch.continent}
                      </Badge>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-amber-400 transition-colors shrink-0 mt-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Gallery */}
      {gallery?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-12" data-testid="gallery-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-amber-400" />
              Gallery
            </h2>
            <Button
              variant="outline"
              size="sm"
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs"
              onClick={() => setGalleryOpen(true)}
              data-testid="view-all-gallery"
            >
              View All ({gallery.length})
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {gallery.slice(0, 8).map((img, i) => (
              <div
                key={i}
                className="aspect-square rounded-xl overflow-hidden border border-white/10 cursor-pointer hover:border-amber-500/40 transition-all group"
                onClick={() => setSelectedImage(img)}
              >
                <img
                  src={img}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Store */}
      {store_items?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-12" data-testid="store-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-amber-400" />
              Media Store
            </h2>
            <a
              href="https://www.bishopmanjoro.org/store.php"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-400 hover:text-amber-300 text-xs flex items-center gap-1 transition-colors"
            >
              Visit Store <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {store_items.map((item, i) => (
              <Card key={i} className="bg-[#1a1f2e] border-white/10 overflow-hidden" data-testid={`store-item-${i}`}>
                <CardContent className="p-0">
                  <div className="flex gap-4 p-4">
                    <div className="h-20 w-20 rounded-lg overflow-hidden shrink-0 bg-slate-800">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-white truncate">{item.title}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{item.author}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]">
                          {item.type}
                        </Badge>
                        <span className="text-amber-400 text-sm font-semibold">{item.price}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Affiliations */}
      <section className="max-w-6xl mx-auto px-4 py-12 pb-20">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Users className="h-5 w-5 text-amber-400" />
          Affiliations & Memberships
        </h2>
        <div className="flex flex-wrap gap-3">
          {bishop.affiliations.map((aff, i) => (
            <div key={i} className="px-4 py-2.5 rounded-xl bg-[#1a1f2e] border border-white/10 text-slate-300 text-sm">
              {aff}
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#0c0f14]">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <p className="text-slate-600 text-xs">
            Data sourced from{' '}
            <a href="https://www.bishopmanjoro.org" target="_blank" rel="noopener noreferrer" className="text-amber-500/60 hover:text-amber-400 transition-colors">
              bishopmanjoro.org
            </a>
          </p>
        </div>
      </footer>

      {/* Gallery Dialog */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-4xl bg-[#1a1f2e] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Gallery</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
            {gallery?.map((img, i) => (
              <div
                key={i}
                className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:ring-2 ring-amber-500/40 transition-all"
                onClick={() => { setGalleryOpen(false); setSelectedImage(img); }}
              >
                <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={selectedImage}
            alt="Gallery"
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default BishopManjoroPage;
